<svelte:options
	customElement={{
		tag: 'rf-light-table-element',
		shadow: 'open', // shadow: 'open'
	}}
/>

<script module lang="ts">
	export interface ReframeLightTableElementProps {
		x?: string;
		y?: string;
		width?: string;
		height?: string;
		rotation?: string;
		order?: string;
		opacity?: string;
		debug?: boolean;
		'static-positioning'?: boolean;
	}
</script>

<script lang="ts">
	/**
	 * Imports
	 */
	import { onMount, onDestroy } from 'svelte';
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import { get } from 'svelte/store';
	import { createEventDispatcher } from 'svelte';
	import classNames from 'classnames';
	import { onEvent } from '@src/utils/events';

	/**
	 * Props
	 */
	const { debug, 'static-positioning': staticPositioning } = $props();

	/**
	 * Variables
	 */
	const dispatch = createEventDispatcher();
	let resizeObserver: ResizeObserver | null = null;
	let container: HTMLElement | null = $state(null);
	let hasOtherElement = $state(false);

	/**
	 * Composables
	 */
	const { element, forEachSlottedElement } = useSlottedElements();

	/**
	 * Events
	 */
	const handleMouseDown = (event: MouseEvent) => {
		dispatch('grabber-mousedown', event);
	};

	const onResize = () => {
		const currentElement = get(element);
		if (!currentElement) return;

		if (hasOtherElement) {
			currentElement.style.width = 'inherit';
			currentElement.style.height = 'auto';
		}
	};

	/**
	 * Lifecycle
	 */
	onMount(() => {
		const currentElement = get(element);
		if (!currentElement) return;

		forEachSlottedElement((element) => {
			if (element.tagName === 'RF-CANVAS') {
				element.setAttribute('fit', 'contain');
			} else {
				hasOtherElement = true;
			}
		});

		onEvent('rf-page-resize', onResize);

		if (container) {
			resizeObserver = new ResizeObserver((entries) => {
				dispatch('element-resize', {
					width: entries[0].contentRect.width,
					height: entries[0].contentRect.height,
				});
			});
			resizeObserver.observe(container);
		}
	});

	onDestroy(() => {
		if (resizeObserver) {
			resizeObserver.disconnect();
		}
	});
</script>

<div
	bind:this={$element}
	class={classNames('rf-light-table-element', {
		'rf-light-table-element--debug': debug,
		'rf-light-table-element--static': staticPositioning,
		'rf-light-table-element--other': hasOtherElement,
	})}
	part="light-table-element"
>
	<div class="rf-light-table-element__content" bind:this={container}>
		<div class="rf-light-table-element__grabber" onmousedown={handleMouseDown} role="button" tabindex="0"></div>
		<slot></slot>
	</div>
</div>

<style lang="scss" global>
	@use 'assets/scss/core/rf-breakpoints' as breakpoint;
	.rf-light-table-element {
		width: 100%;
		height: 100%;

		&__content {
			position: relative;
			overflow: hidden;
			height: 100%;
			width: 100%;

			--rf-canvas-height: 100%;
			.rf-light-table-element--debug & {
				resize: both;
				&::after {
					content: '';
					width: 15px;
					height: 15px;
					background-color: rgba(255, 255, 255, 1);
					z-index: 10;
					position: absolute;
					bottom: 0;
					right: 0;
					pointer-events: none;
				}
				@container rf-light-table (width < #{breakpoint.$s}) {
					resize: unset;
					&::after {
						display: none;
					}
				}
			}
		}
		&--other {
			height: auto !important;
			&.rf-light-table-element--debug {
				.rf-light-table-element__content {
					resize: horizontal;
				}
			}
		}

		&__grabber {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			cursor: grab;
			z-index: 11;
			.rf-light-table-element--static & {
				display: none;
				pointer-events: none;
			}
		}
	}

	:host(.rf-light-table-element--dragging) {
		.rf-light-table-element__grabber {
			cursor: grabbing;
		}
	}

	::host:not(.rf-light-table-element--in-inspector) {
		@container rf-light-table (width < #{breakpoint.$s}) {
			.rf-light-table-element__grabber {
				display: none;
			}
		}
	}

	:host(.rf-light-table-element--static) {
		.rf-light-table-element__grabber {
			cursor: default;
		}
	}
</style>
